import React, { useRef, Fragment, useState, useEffect, useMemo, } from 'react';
import { FormGroup, Label, InputGroup, Input, Button, Modal, ModalBody, ModalFooter, Form, } from 'reactstrap';
import { orderBy, chunk, groupBy, sum, sumBy, sortBy, isEmpty, pick, get, keyBy, omit, mapValues, } from 'lodash';
import { useDebounce, useToggle, useList, } from 'react-use';
import { toast, } from 'react-toastify';
import { Link, } from 'react-router-dom';
import { format as formatDate } from 'date-fns';
import Select from 'react-select';
import jsbarcode from 'jsbarcode';
import { jsPDF } from 'jspdf';
import { PDFDocument, } from 'pdf-lib';
import dedent from 'dedent';
import fileDownload from 'js-file-download';
import classnames from 'classnames';

import env from '../../env';
import okSoundSrc from '../../sounds/ok.mp3';
import ngSoundSrc from '../../sounds/ng.mp3';
import { drawProductLabel, drawAmazonProductLabel } from '../../label';
import firebase, { functions } from '../../firebase';
import { batch, } from '../../shared/firebase';
import { isMobile, safePrefix, } from '../../util';
import { fieldDisplayValue, } from '../../shared/util';
import useFormState from '../hooks/useFormState';
import Field from '../Field';
import { prefectures, } from '../../shared/config';
import { idWithTenant, } from '../../shared/util';
import { labelString, nekoToggleValues, } from '../../shared/models/product';
import { fields as combiningRequestFields, getCombinedItems, computeGBFcSortNumber, computeDocumentNumberForBarcode, } from '../../shared/models/combiningRequest';
import { statuses, } from '../../shared/models/inventory';
import ReceivingRequestDetailModal from './ReceivingRequestDetailModal';
import ListForm from '../ListForm';
import PendingItemForm from '../forms/PendingItemForm';
import useCollectionSubscription from '../hooks/useCollectionSubscription';
import useCollectionsSubscription from '../hooks/useCollectionsSubscription';
import useCollectionsFetch from '../hooks/useCollectionsFetch';
import useDocumentsFetch from '../hooks/useDocumentsFetch';
import useDocumentSubscription from '../hooks/useDocumentSubscription';
import { ResponsiveTableRow, ResponsiveTableColumn } from '../ResponsiveTable';

const labelWidth = 52.5;
const labelHeight = 29.7;
const { keys, entries, } = Object;
const printBarcode = functions.httpsCallable('printBarcode');
const completeInboundShipment = functions.httpsCallable('completeInboundShipment');
const db = firebase.firestore();
const tenantsRef = db.collection('tenants');
const inventoriesRef = db.collection('inventories');
const combiningRequestsRef = db.collectionGroup('combiningRequests');

export default function CombiningModal(props) {
  const { type, isOpen, locations, enablesQuantity, } = props;

  const textValueInput = useRef(null);
  const [textValue, setTextValue] = useState('');
  const [, tenantId, combiningRequestId] = textValue.match(new RegExp(`(${env('TENANT_PREFIX')}\\d+)-(C\\d+)`)) || [];

  const matchedCombiningRequest = useDocumentSubscription(tenantId && combiningRequestId && tenantsRef.doc(tenantId).collection('combiningRequests').doc(combiningRequestId), [tenantId, combiningRequestId]);

  const onCompleted = () => {
    setTextValue('');
    textValueInput.current.focus();
  };

  return (
    <Modal isOpen={isOpen} style={{ minWidth: !isMobile() && 1000 }}>
      <div className="modal-header">
        <div className="w-100 d-flex justify-content-between">
          <h5 className="modal-title">
            ピッキング
          </h5>
        </div>
      </div>
      <ModalBody>
        <FormGroup>
          <Label>加工依頼ID</Label>
          <Input innerRef={textValueInput} value={textValue} onChange={_ => setTextValue(_.target.value)} />
        </FormGroup>
        {
          !isEmpty(textValue) && (
            matchedCombiningRequest != null ? (
              <CombiningRequestCard combiningRequest={matchedCombiningRequest} locations={locations} onCompleted={onCompleted} enablesQuantity={enablesQuantity} />
            ) : (
              <div className="text-danger">
                加工依頼が見つかりません
              </div>
            )
          )
        }
      </ModalBody>
      <ModalFooter>
        <Button className="cancel" color="secondary" onClick={props.onClickClose}>閉じる</Button>
      </ModalFooter>
    </Modal>
  );
};

function CombiningRequestCard (props) {
  const { locations, combiningRequest, onCompleted, enablesQuantity, } = props;
  const ngAudioEl = useRef(null);
  const okAudioEl = useRef(null);
  const locationsById = keyBy(locations, 'id');
  const [isProcessing, toggleProcessing] = useToggle(false);
  const [isFinishing, toggleFinishing] = useToggle(false);
  const [showsAll, toggleShowsAll] = useToggle(false);
  const tenantRef = combiningRequest.ref.parent.parent;
  const tenant = useDocumentSubscription(tenantRef, [tenantRef.id]);
  let sortedPickedInventoriesRef = inventoriesRef.where('tenantId', '==', tenantRef.id).where('combiningRequestId', '==', combiningRequest.id).where('status', 'in', ['picked', 'shipped']).orderBy('pickedAt');
  if(!showsAll) sortedPickedInventoriesRef = sortedPickedInventoriesRef.limit(3);
  const sortedPickedInventories = useCollectionSubscription(sortedPickedInventoriesRef, [combiningRequest.id, showsAll]);
  const pickedInventoriesCounts = combiningRequest.items.reduce((x, { productId }) => {
    return { ...x, [productId]: (combiningRequest[['inventoryIds', productId].join('-')] || []).length, };
  }, {});
  const totalPickedCount = sum(Object.values(pickedInventoriesCounts));
  const itemsWithCandidateCount = combiningRequest.items.map(_ => ({ ..._, candidateCount: _.quantity - get(pickedInventoriesCounts, [_.productId], 0) }));
  const totalRequiredCount = sumBy(combiningRequest.items, 'quantity');
  // NOTE 同時作業状況を反映するため、subscする必要がある
  const products = useCollectionsSubscription(chunk(combiningRequest.items, 10).map(items => tenantRef.collection('products').where(firebase.firestore.FieldPath.documentId(), 'in', items.map(_ => _.productId))), [combiningRequest.items.map(_ => _.productId).join(',')], { initialItems: null });
  const productsById = keyBy(products, 'id');
  const allocatedInventoryIds = combiningRequest.allocatedInventoryIds || [];
  const allPickedIds = new Set(combiningRequest.items.flatMap(_ => combiningRequest[`inventoryIds-${_.productId}`] || []));
  const candidateProductIds = new Set(itemsWithCandidateCount.filter(_ => _.candidateCount > 0).map(_ => _.productId));
  const candidateIds = allocatedInventoryIds.filter(_ => candidateProductIds.has(_.productId) && !allPickedIds.has(_.inventoryId)).map(_ => _.inventoryId);
  const { items: candidateInventories, isLoading } = useCollectionsFetch(
    chunk(candidateIds.slice(0, showsAll ? Infinity : 3), 10).map(_ => inventoriesRef.where(firebase.firestore.FieldPath.documentId(), 'in', _)),
    [combiningRequest.id, showsAll, totalPickedCount, JSON.stringify(allocatedInventoryIds)],
    { detail: true },
  );
  // NOTE: ロケ順 → 同一ロケ内は先入れ先出し(shelvedAt昇順)
  const sortedCandidateInventories = sortBy(candidateInventories.filter(_ => _.status === 'shelved' && _.isNg === (combiningRequest.type === 'return')), [_ => _.locationKey ?? '', _ => _.shelvedAt?.toDate?.()?.getTime?.() ?? 0]);
  const combinedItems = getCombinedItems(combiningRequest);
  const combinedProducts = useCollectionsSubscription(chunk(combinedItems, 10).map(items => tenantRef.collection('products').where(firebase.firestore.FieldPath.documentId(), 'in', items.map(_ => _.productId))), [combinedItems.map(_ => _.productId).join(',')], { initialItems: null });
  const combinedProductsById = keyBy(combinedProducts, 'id');
  const isSatisfied = itemsWithCandidateCount.every(_ => _.candidateCount === 0);

  const sortedInventories = [...sortedCandidateInventories, ...sortedPickedInventories];
  const rowsContent = useMemo(() => {
    return (showsAll ? sortedInventories : sortedInventories.slice(0, 3)).map((inventory, i) => {
      const { id, ref, locationId, productId } = inventory;
      const location = locationsById[locationId];
      const product = productsById[productId];

      return location != null && product != null && (
        <Row key={i} {...{ id, inventoryRef: ref, combiningRequest, location, product, inventory, }} />
      );
    });
  }, [showsAll, products, locations, sortedInventories.map(_ => _.id).join('')]);

  const [quantity, setQuantity] = useState(1);
  const [barcodeValue, setBarcodeValue] = useState('');
  const isInvalidQuantity = quantity != null && (quantity <= 0 || quantity > 400);
  const onChangeBarcodeInfoValue = async (e) => {
    setBarcodeValue(e.target.value);
  };
  const playAudio = (audio) => {
    audio.currentTime = 0;
    audio.play();
  };
  useDebounce(async (e) => {
    if(isEmpty(barcodeValue)) return;

    setBarcodeValue('');
    setQuantity(1);
    const matchedProduct = products.find(_ => [_.fnsku, idWithTenant(_.ref)].includes(safePrefix(barcodeValue).replace(/-N$/, '')));
    if(matchedProduct == null) return playAudio(ngAudioEl.current);

    const matchedInventories = enablesQuantity ? sortedCandidateInventories.filter(_ => _.productId === matchedProduct.id).slice(0, quantity) : sortedCandidateInventories.slice(0, 1).filter(_ => _.productId === matchedProduct.id);
    if(matchedInventories.length === 0) return playAudio(ngAudioEl.current);
    if(matchedInventories.length < quantity) return playAudio(ngAudioEl.current);

    toggleProcessing(true);
    const batch = db.batch();
    matchedInventories.map((matchedInventory) => {
      batch.update(matchedInventory.ref, {
        combiningRequestId: combiningRequest.id,
        shippingRequestId: null,
        pickedAt: new Date(),
        status: 'picked',
      });
    });
    batch.update(matchedProduct.ref, {
      ngInventoryIds: firebase.firestore.FieldValue.arrayRemove(...matchedInventories.map(_ => _.id)),
      goodInventoryIds: firebase.firestore.FieldValue.arrayRemove(...matchedInventories.map(_ => _.id)),
    });
    batch.update(combiningRequest.ref, {
      [['inventoryIds', matchedProduct.id].join('-')]: firebase.firestore.FieldValue.arrayUnion(...matchedInventories.map(_ => _.id)),
      inventoryIds: firebase.firestore.FieldValue.arrayUnion(...matchedInventories.map(_ => _.id)),
    });
    await batch.commit();
    toggleProcessing(false);
    playAudio(okAudioEl.current);
  }, 300, [barcodeValue]);

  const onClickPrintBarcode = async () => {
    try {
      const allPdfDoc = await PDFDocument.create();
      await combinedItems.reduce(async (x, { productId, quantity }) => {
        await x;

        const combinedProduct = combinedProductsById[productId];
        if(combinedProduct == null) return;

        const canvas = document.createElement('canvas');
        ((combinedProduct.isAmazonProduct && combinedProduct.fnsku) ? drawAmazonProductLabel : drawProductLabel)(canvas, labelWidth, labelHeight, combinedProduct);
        const imageData = canvas.toDataURL('image/jpeg', 0.2);
        const doc = new jsPDF({
          orientation: 'landscape',
          format: [labelWidth, labelHeight]
        });
        doc.addImage(imageData, 'JPEG', 0, 0, labelWidth, labelHeight);
        const pdfData = doc.output('datauri');
        const pdfDoc = await PDFDocument.load(pdfData);
        await Array(quantity).fill().reduce(async (y, _, i) => {
          await y;

          const [page] = await allPdfDoc.copyPages(pdfDoc, keys(pdfDoc.getPages()));
          await allPdfDoc.addPage(page);
        }, Promise.resolve());
      }, Promise.resolve());

      const content = await allPdfDoc.saveAsBase64({ dataUri: true })
      // DEV
      // fileDownload(await allPdfDoc.save(), 'hoge.pdf');
      // return;

      await printBarcode({
        type: 'receivementProduct',
        title: '完成商品ラベル',
        imageData: content,
      });
    } catch(e) {
      console.error(e);
      toast.error('失敗しました');
    }
  };
  const onClickCompleted = async () => {
    toggleFinishing(true);
    try {
      if(combinedItems.some(_ => combinedProductsById[_.productId] == null)) {
        toast.error('完成商品の読み込みが完了していません');
        toggleFinishing(false);
        return;
      }
      const expandedCombinedItems = combinedItems.flatMap(({ productId, quantity }) => {
        return Array(quantity).fill().map(_ => productId);
      });
      const _batch = db.batch();
      _batch.update(combiningRequest.ref, { status: 'combined', combinedAt: new Date(), completedAt: new Date(), });
      await batch(db, expandedCombinedItems, (batch, productId) => {
        const combinedProduct = combinedProductsById[productId];
        batch.set(inventoriesRef.doc(), {
          tenantId: tenant.id,
          combiningRequestId: combiningRequest.id,
          receivingRequestId: null,
          productId: combinedProduct.id,
          productBarcodeInfo: labelString(combinedProduct),
          productBarcodeInfoType: (combinedProduct.isAmazonProduct && combinedProduct.fnsku) ? 'fnsku' : 'id',
          locationId: null,
          status: 'received',
          isNg: false,
          createdAt: new Date(),
        });
      }, 499, { firstBatch: _batch });
      await onCompleted();
    } catch(e) {
      console.error(e);
      toast.error('失敗しました');
    }
    toggleFinishing(false);
  };
  const onClickNeko = async (combinedProduct) => {
    const values = nekoToggleValues(combinedProduct);
    const suffix = ({ true: 'に指定', false: '指定を解除' })[values.isNeko];
    if(!window.confirm(`本当にネコポス${suffix}しますか？`)) return;

    await combinedProduct.ref.update(values);
    toast.success(`ネコポス${suffix}しました`);
  };

  return combinedProducts != null && combinedProducts.length > 0 && (
    <div>
      <hr />
      <h5>
        見つかった加工依頼
      </h5>
      <div>
        <div className="d-flex justify-content-between gap-3">
          <div>
            <h4>
              {idWithTenant(combiningRequest.ref)}
            </h4>
            <div className="my-2">
              <FormGroup check>
                <Label check>
                  <Input type="checkbox" checked={showsAll} onChange={_ => toggleShowsAll(_.target.checked)} />
                  すべて表示
                </Label>
              </FormGroup>
            </div>
          </div>
          <div className="d-flex gap-1">
            <FormGroup className={!enablesQuantity && 'd-none'}>
              <Label className="small text-muted">数量</Label>
              <Input type="number" className={classnames('text-right', { 'is-invalid': isInvalidQuantity })} step="1" max={400} value={quantity} onChange={_ => setQuantity(_.target.value === '' ? null : parseInt(_.target.value, 10))} style={{ width: 'auto' }} />
            </FormGroup>
            <FormGroup>
              <Label className="small text-muted">商品バーコード</Label>
              <Input value={barcodeValue} onChange={onChangeBarcodeInfoValue} autoFocus readOnly={isProcessing || isLoading} disabled={isInvalidQuantity} />
            </FormGroup>
          </div>
        </div>
        <div className="d-flex justify-content-end mt-1">
          <div>
            {totalPickedCount} / {totalRequiredCount} ({totalRequiredCount - totalPickedCount})
          </div>
        </div>
        <div className="overflow-auto mb-2" style={{ maxHeight: 'calc(100vh - 400px)', }}>
          <table className={`table ${isMobile() ? '' : 'table-bordered'}`}>
            {
              !isMobile() && (
                <thead>
                  <tr>
                    <th style={{ minWidth: 80 }}>棚キー</th>
                    <th style={{ minWidth: 80 }}>バーコード</th>
                    <th style={{ minWidth: 100 }}>状態</th>
                    <th style={{ minWidth: 150 }}>商品名</th>
                  </tr>
                </thead>
              )
            }
            <tbody>
              {rowsContent}
            </tbody>
          </table>
        </div>
        <div className="my-2">
          <h6>完成商品</h6>
          {
            combinedItems.map(({ productId, quantity }) => {
              const combinedProduct = combinedProductsById[productId];
              return combinedProduct != null && (
                <div key={productId} className="d-flex align-items-center gap-2">
                  <span className={classnames('fas fa-cat cursor-pointer', { 'text-info': combinedProduct.isNeko, 'text-muted': !combinedProduct.isNeko })} onClick={_ => onClickNeko(combinedProduct)} />
                  <span>[{idWithTenant(combinedProduct.ref)}] {combinedProduct.name} × {quantity}</span>
                  {!combinedProduct.isNeko && combinedProduct.requestsNeko && <span className="text-danger">ネコポス希望</span>}
                </div>
              );
            })
          }
        </div>
        <div className="d-flex gap-1">
          <Button onClick={onClickPrintBarcode}>
            ラベル発行
          </Button>
          <Button color="success" disabled={!isSatisfied || isFinishing} onClick={onClickCompleted}>
            完了
          </Button>
        </div>
      </div>
      <audio src={ngSoundSrc} ref={ngAudioEl} />
      <audio src={okSoundSrc} ref={okAudioEl} />
    </div>
  );
}

function Row (props) {
  const { id, inventoryRef, combiningRequest, location, product, } = props;
  const inventory = useDocumentSubscription(inventoryRef, [id]);
  const { label: statusLabel, shippingColor, } = statuses[inventory && inventory.status] || {};

  return inventory != null && (
    <ResponsiveTableRow key={id} className={isMobile() ? `position-relative bg-${shippingColor}` : `table-${shippingColor}`}>
      <ResponsiveTableColumn label="棚キー">
        {location.sortKey}
      </ResponsiveTableColumn>
      <ResponsiveTableColumn label="バーコード">
        {inventory.productBarcodeInfo}
      </ResponsiveTableColumn>
      <ResponsiveTableColumn label="状態">
        <span className={`badge badge-${shippingColor}`}>
          {get(statuses, [inventory.status, 'label'])}
        </span>
      </ResponsiveTableColumn>
      <ResponsiveTableColumn label="商品名">
        {product.name}
      </ResponsiveTableColumn>
      {
        isMobile() && ['picked', 'shipped'].includes(inventory.status) && (
          <div className="position-absolute d-flex align-items-center justify-content-end" style={{ top: 0, right: 0, bottom: 0, left: 0, }}>
            <div className="border rounded-circle border-danger text-danger d-flex align-items-center justify-content-center" style={{ width: 70, height: 70, fontSize: 40, marginRight: 30, }}>
              済
            </div>
          </div>
        )
      }
    </ResponsiveTableRow>
  );
}
